import { API_KEY_SCOPES, type TAPIKeyScope } from '@feathr/blackbox';

/** A resource with a read and a write scope (feed is excluded — read-only). */
export interface IResourceGroup {
  resource: string;
  read: TAPIKeyScope;
  write: TAPIKeyScope;
}

/** The read-only scope that has no write counterpart. */
const READ_ONLY_SCOPES: TAPIKeyScope[] = ['feed:read'];

/** Splits a `resource:action` scope into its parts. */
function parseScope(scope: TAPIKeyScope): { resource: string; action: string } {
  const [resource, action] = scope.split(':');
  return { action, resource };
}

/** Read/write resource groups derived from API_KEY_SCOPES (excludes read-only feed). */
export function getResourceGroups(): IResourceGroup[] {
  const byResource = new Map<string, Partial<IResourceGroup>>();

  API_KEY_SCOPES.forEach((scope) => {
    if (READ_ONLY_SCOPES.includes(scope)) {
      return;
    }
    const { resource, action } = parseScope(scope);
    const group = byResource.get(resource) ?? { resource };
    if (action === 'read') {
      group.read = scope;
    } else if (action === 'write') {
      group.write = scope;
    }
    byResource.set(resource, group);
  });

  return [...byResource.values()].filter(
    (group): group is IResourceGroup => group.read !== undefined && group.write !== undefined,
  );
}

/** Returns the full selectable scope vocabulary (never includes the `*` wildcard). */
export function getAllScopes(): TAPIKeyScope[] {
  return [...API_KEY_SCOPES];
}

/** Selected set → scopes array from the canonical vocabulary (never emits `*`). */
export function selectedScopesToArray(selected: Set<TAPIKeyScope>): TAPIKeyScope[] {
  return API_KEY_SCOPES.filter((scope) => selected.has(scope));
}

/** A checkbox's tri-state value: fully selected, partially selected, or none. */
export type TTriStateValue = boolean | 'indeterminate';

/** Parent/"All" checkbox value: true (all), 'indeterminate' (some), or false (none). */
export function getTriStateValue(allSelected: boolean, someSelected: boolean): TTriStateValue {
  if (allSelected) {
    return true;
  }
  if (someSelected) {
    return 'indeterminate';
  }
  return false;
}
